import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal, ModalDismissReasons, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { copyToClipboard } from '../../shared/app-util';
import { ActivatedRoute } from '@angular/router';
import { CustomerService } from '../services/customer.service';
import { OktaConfiguration } from '../models/okta-configuration';
import { Subscription } from 'rxjs';
import { Alert } from '../../shared/alert.model';
import { getAlertMessage } from '../../shared/api-loaders';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

enum TypeOfData {
  ClientID = 'clientId',
  AuthServerId = 'authServerId',
  ClientSecret = 'clientSecret',
  Issuer = 'issuer'
}

@Component({
    selector: 're-okta-configuration',
    templateUrl: './okta-configuration.component.html',
    styleUrls: ['./okta-configuration.component.scss'],
    standalone: false
})
export class OktaConfigurationComponent implements OnInit, OnDestroy {
  alert: Alert;

  private _clientId: string = '';
  private _clientSecret: string = '';
  private _issuer: string = '';

  customerName: string = '';
  crmId: string = '';

  typeOfData = TypeOfData;
  editableForm: boolean = true;
  loading: boolean = false;
  editing: boolean = true;
  showEditButton : boolean = false;

  @ViewChild('modalContent') templateRef: TemplateRef<any>;
  @ViewChild('modalContent1') templateRef1: TemplateRef<any>;

  public oktaConfigurationForm: UntypedFormGroup;
  oktaDetailsSubscription: Subscription;

  signInRedirectUrisResponse: string[];
  signOutRedirectUrisResponse: string[];

  private snfString1 = `{{customer SNF url}}/callback?client_name={{crmid}}`;
  private snfString2 = `{{customer SNF url}}/callback?client_name={{crmid}}matrixcare`;

  private caString1 = `{{customer CA url}}/careassist/callback?client_name={{crmid}}`;
  private caString2 = `{{customer CA url}}/careassist/callback?client_name={{crmid}}matrixcare`;
  private caString3 = `{{customer CA url}}/careassist/callback?client_name=matrixcare`;

  private slString1 = `{{customer SL url}}/Auth/SingleSignOnValidateCallback`;
  private slString2 = `{{customer SL url}}/Support/SingleSignOnValidateCallback`;

  private intakeString1 = `{{customer Intake url}}/Auth/SingleSignOnValidateCallback`;
  private intakeString2 = `{{customer Intake url}}/Support/SingleSignOnValidateCallback`;

  private mealTracker1 = `{{Meal Tracker url}}/auth/SingleSignOnValidateCallback`;

  private snfSignout1 = `{{customer SNF url}}/core/logout.action?dest=/logout_error_sso.html`;
  private snfSignout2 = `{{customer SNF url}}/core/logout.action`;

  private caString4 = `{{customer CA url}}/careassist/login?logout`;

  private slString3 = `{{customer SL url}}/SessionLogout/LogOut`;
  private slString4 = `{{customer SL url}}`;

  private intakeString3 = `{{customer Intake url}}/SessionLogout/LogOut`;
  private intakeString4 = `{{customer Intake url}}/`;
  private intakeString5 = `{{customer Intake url}}/SsoError.aspx`;

  private mealTracker2 = `{{Meal Tracker url}}`;

  private _infoData: Array<SafeHtml> = new Array<SafeHtml>();
  myInfoData: Array<string> = [
                                this.snfString1, this.snfString2,
                                this.caString1, this.caString2, this.caString3,
                                this.slString1, this.slString2,
                                this.intakeString1, this.intakeString2,
                                this.mealTracker1,
                                this.snfSignout1, this.snfSignout2,
                                this.caString4,
                                this.slString3, this.slString4,
                                this.intakeString3, this.intakeString4, this.intakeString5,
                                this.mealTracker2
                              ];

  public get info(): SafeHtml[] {
    return this._infoData;
  }

  public get clientId(): string {
    return this._clientId;
  }

  public get clientSecret(): string {
    return this._clientSecret;
  }

  public get issuer(): string {
    return this._issuer;
  }

  public set clientId(id: string) {
    this._clientId = id;
  }

  public set clientSecret(secret: string) {
    this._clientSecret = secret;
  }

  public set issuer(secret: string) {
    this._issuer = secret;
  }

  constructor(private formBuilder: UntypedFormBuilder, private location: Location, private modalService: NgbModal,
              private customerService: CustomerService, private routeParams: ActivatedRoute, private sanitizer: DomSanitizer) {
  }

  public fetchCustomerOktaDetails() {
    this.oktaDetailsSubscription = this.customerService.getCustomerOktaDetails(this.crmId).subscribe({
      next: (details) => {
        this.loading = false;
        this.clientId = details.clientId;
        this.issuer = details.issuer;
        this.clientSecret = details.clientSecret;
        this.signInRedirectUrisResponse = details.signInRedirectUris;
        this.signOutRedirectUrisResponse = details.signOutRedirectUris;
        this.processForm();
      },
      error: (error) => {
        this.alert = getAlertMessage(error);
        this.loading = false;
      }
    });
  }

  ngOnInit() {
    const canEdit = this.routeParams.snapshot.paramMap.get('editable');
    this.init(canEdit);
    this.myInfoData.forEach((data) => {
      this._infoData.push(this.sanitizer.sanitize(1, data));
    });
  }

  async init(canEdit: string) {
    this.loading = true;
    this.customerName = this.routeParams.snapshot.paramMap.get('customerName');
    this.crmId = this.routeParams.snapshot.paramMap.get('crmId');

    this.editableForm = (canEdit === 'true') ? true : false;
    if(this.editableForm) {
      this.oktaConfigurationForm = this.formBuilder.group({
        signInRedirectUris: this.formBuilder.array([ this.addSignInURI() ]),
        signOutRedirectUris: this.formBuilder.array([ this.addSignOutURI() ])
      });
      this.loading = false;
    } else {
      this.editing = false;
      this.showEditButton = true;
      await this.fetchCustomerOktaDetails();
      this.oktaConfigurationForm = this.formBuilder.group({
        signInRedirectUris: this.formBuilder.array([]),
        signOutRedirectUris: this.formBuilder.array([])
      });
      this.processUris('signin', []);
      this.processUris('signout', []);
    }
  }

  processForm() {
    if(this.signInRedirectUrisResponse) {
      if(this.signInRedirectUrisResponse.length > 0) {
        this.signInRedirectUrisinFormArray.clear();
      }
    }
    if(this.signOutRedirectUrisResponse) {
      if(this.signOutRedirectUrisResponse.length > 0) {
        this.signOutRedirectUrisinFormArray.clear();
      }
    }
    this.processUris('signin', this.signInRedirectUrisResponse);
    this.processUris('signout', this.signOutRedirectUrisResponse);
    this.loading = false;
  }

  processUris(type: string, uris: string[]) {
    if(type === 'signin') {
      if(uris) {
        uris.forEach(uri => this.addNewSignInUri(uri));
      }
    } else {
      if(uris) {
        uris.forEach(uri => this.addNewSignOutUri(uri));
      }
    }
  }

  get signInRedirectUrisinFormArray(): FormArray {
    return this.oktaConfigurationForm.get('signInRedirectUris') as FormArray;
  }

  get signOutRedirectUrisinFormArray(): FormArray {
    return this.oktaConfigurationForm.get('signOutRedirectUris') as FormArray;
  }

  addSignInURI(value: string = ''): UntypedFormGroup {
    return this.formBuilder.group({
      signInUri: [{value: value, disabled: !this.editing }, Validators.required]
    });
  }

  addSignOutURI(value: string = ''): UntypedFormGroup {
    return this.formBuilder.group({
      signOutUri: [{value: value, disabled: !this.editing }, Validators.required]
    });
  }

  addNewSignInUri(value: string = ''): void {
    (<FormArray>this.oktaConfigurationForm.get("signInRedirectUris")).push(this.addSignInURI(value));
  }

  removeSignInUri(index: number): void {
    const mysignInRedirectUris = this.oktaConfigurationForm.get('signInRedirectUris') as FormArray;
    mysignInRedirectUris.removeAt(index);
  }

  addNewSignOutUri(value: string = ''): void {
    (<FormArray>this.oktaConfigurationForm.get("signOutRedirectUris")).push(this.addSignOutURI(value));
  }

  removeSignOutUri(index: number): void {
    const mySignOutRedirectUris = this.oktaConfigurationForm.get('signOutRedirectUris') as FormArray;
    mySignOutRedirectUris.removeAt(index);
  }

  resetForm() {
    this.signInRedirectUrisinFormArray.clear();
    this.signOutRedirectUrisinFormArray.clear();
    this.oktaConfigurationForm.reset();
    this.addNewSignInUri();
    this.addNewSignOutUri();
  }

  createStringUris(type: string, uriArray: any[]) {
    const stringUris = [];
    if(uriArray) {
      if(type === 'signin') {
        uriArray.forEach(uri => stringUris.push(uri.signInUri));
      } else {
        uriArray.forEach(uri => stringUris.push(uri.signOutUri));
      }
    }
    return stringUris;
  }

  onSubmit() {
    this.loading = true;
    const oktaData: OktaConfiguration = {
      crmId: this.crmId,
      signInRedirectUris: this.createStringUris('signin', this.oktaConfigurationForm.value.signInRedirectUris),
      signOutRedirectUris: this.createStringUris('signout', this.oktaConfigurationForm.value.signOutRedirectUris)
    }
    this.customerService.generateCustomerOktaDetails(oktaData).subscribe({
        next: (response: OktaConfiguration) => {
          this.clientId = response.clientId;
          this.issuer = response.issuer;
          this.clientSecret = response.clientSecret;
          this.signInRedirectUrisResponse = response.signInRedirectUris;
          this.signOutRedirectUrisResponse = response.signInRedirectUris;
          if(this.editableForm){
            this.openModal();
          }
          this.editableForm = false;
          this.editing = false;
          this.enableDisableFormInputs(false);
          this.showEditButton = true;
          this.loading = false;
        },
        error: (error) => {
          this.alert = getAlertMessage(error);
          this.loading = false;
        }
    });
  }

  back() {
    if(!this.showEditButton && !this.editableForm){
      this.showEditButton = true;
      this.editing = false;
      this.resetForm();
      this.processForm();
      return;
    }
    this.location.back();
  }

  openModal() {
    const options: NgbModalOptions = { size: 'lg' };
    this.modalService.open(this.templateRef, options);
  }

  copyText(data: string) {
    switch (data) {
      case TypeOfData.ClientID:
       copyToClipboard(this.clientId);
        break;
      case TypeOfData.ClientSecret:
       copyToClipboard(this.clientSecret);
        break;
      case TypeOfData.Issuer:
       copyToClipboard(this.issuer);
        break;
      default:
        break;
    }
  }

  openInfoModal() {
    const options: NgbModalOptions = { size: 'lg' };
    this.modalService.open(this.templateRef1, options);
  }

  edit() {
    this.editing = true;
    this.showEditButton = false;
    this.enableDisableFormInputs(true);
  }

  enableDisableFormInputs(enable: boolean){
    if(enable){
      this.oktaConfigurationForm.get('signInRedirectUris').enable();
      this.oktaConfigurationForm.get('signOutRedirectUris').enable();
    } else {
      this.oktaConfigurationForm.get('signInRedirectUris').disable();
      this.oktaConfigurationForm.get('signOutRedirectUris').disable();
    }
  }

  ngOnDestroy(): void {
    if(this.oktaDetailsSubscription) {
      this.oktaDetailsSubscription.unsubscribe();
    }
  }
}
